import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, FileText, Shield, TrendingUp, AlertCircle, CheckCircle,
  Crown, LogOut, User, Bell, Clock, DollarSign, BarChart3, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const InstitutionDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applicationData, setApplicationData] = useState({
    institution_name: '',
    institution_type: 'commercial_bank',
    registration_number: '',
    capital_amount: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    description: ''
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    try {
      const response = await axios.get(`${API}/licenses`, { headers, withCredentials: true });
      setLicenses(response.data);
    } catch (error) {
      console.error('Error fetching licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/licenses/apply`, {
        ...applicationData,
        capital_amount: parseFloat(applicationData.capital_amount)
      }, { headers, withCredentials: true });
      
      toast.success('License application submitted successfully!');
      setApplyOpen(false);
      setApplicationData({
        institution_name: '',
        institution_type: 'commercial_bank',
        registration_number: '',
        capital_amount: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        description: ''
      });
      fetchLicenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Mock compliance data
  const complianceMetrics = [
    { name: 'Capital Adequacy Ratio', value: 14.8, requirement: 10.5, status: 'compliant' },
    { name: 'Liquidity Coverage Ratio', value: 142.5, requirement: 100, status: 'compliant' },
    { name: 'Net Stable Funding Ratio', value: 108.2, requirement: 100, status: 'compliant' },
    { name: 'Leverage Ratio', value: 6.2, requirement: 3, status: 'compliant' }
  ];

  const liquidityData = {
    total_liquidity: 2850000000,
    available_credit: 500000000,
    reserve_balance: 450000000,
    interbank_position: 125000000
  };

  const institutionTypes = [
    { value: 'commercial_bank', label: 'Commercial Bank' },
    { value: 'investment_bank', label: 'Investment Bank' },
    { value: 'fintech', label: 'Fintech Company' },
    { value: 'payment_provider', label: 'Payment Service Provider' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="institution-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary hidden sm:block">Royal CBDC</span>
              <span className="text-xs bg-purple-500/10 text-purple-600 px-2 py-0.5 rounded font-mono uppercase tracking-wider hidden md:block">
                Institution Portal
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-normal text-primary">
              Financial Institution Portal
            </h1>
            <p className="text-muted-foreground mt-1">Banking supervision and compliance management</p>
          </div>
          <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
            <DialogTrigger asChild>
              <Button className="mt-4 md:mt-0 gap-2" data-testid="apply-license-btn">
                <Plus className="h-4 w-4" /> Apply for License
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Banking License Application</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleApply} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Institution Name</Label>
                    <Input
                      value={applicationData.institution_name}
                      onChange={(e) => setApplicationData({ ...applicationData, institution_name: e.target.value })}
                      required
                      data-testid="license-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Institution Type</Label>
                    <Select 
                      value={applicationData.institution_type}
                      onValueChange={(value) => setApplicationData({ ...applicationData, institution_type: value })}
                    >
                      <SelectTrigger data-testid="license-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {institutionTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Registration Number</Label>
                    <Input
                      value={applicationData.registration_number}
                      onChange={(e) => setApplicationData({ ...applicationData, registration_number: e.target.value })}
                      required
                      data-testid="license-reg-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capital Amount (USD)</Label>
                    <Input
                      type="number"
                      value={applicationData.capital_amount}
                      onChange={(e) => setApplicationData({ ...applicationData, capital_amount: e.target.value })}
                      required
                      data-testid="license-capital-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={applicationData.contact_email}
                      onChange={(e) => setApplicationData({ ...applicationData, contact_email: e.target.value })}
                      required
                      data-testid="license-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      value={applicationData.contact_phone}
                      onChange={(e) => setApplicationData({ ...applicationData, contact_phone: e.target.value })}
                      required
                      data-testid="license-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={applicationData.address}
                    onChange={(e) => setApplicationData({ ...applicationData, address: e.target.value })}
                    required
                    data-testid="license-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Business Description</Label>
                  <Textarea
                    value={applicationData.description}
                    onChange={(e) => setApplicationData({ ...applicationData, description: e.target.value })}
                    rows={3}
                    required
                    data-testid="license-description-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="license-submit-btn">
                  Submit Application
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="tab-licenses">Licenses</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
            <TabsTrigger value="liquidity" data-testid="tab-liquidity">Liquidity</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Active Licenses', value: licenses.filter(l => l.status === 'approved').length, icon: <Shield className="h-5 w-5 text-green-500" /> },
                { label: 'Pending Applications', value: licenses.filter(l => l.status === 'pending_review').length, icon: <Clock className="h-5 w-5 text-yellow-500" /> },
                { label: 'Compliance Score', value: '98%', icon: <CheckCircle className="h-5 w-5 text-green-500" /> },
                { label: 'Risk Rating', value: 'Low', icon: <AlertCircle className="h-5 w-5 text-blue-500" /> }
              ].map((stat, idx) => (
                <Card key={idx} className="dashboard-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{stat.label}</span>
                      {stat.icon}
                    </div>
                    <p className="font-mono text-2xl font-semibold" data-testid={`overview-stat-${idx}`}>{stat.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recent Activity */}
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Recent Regulatory Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { title: 'New AML Guidelines Published', date: 'Jan 15, 2024', type: 'regulation' },
                    { title: 'Quarterly Report Deadline Extended', date: 'Jan 12, 2024', type: 'notice' },
                    { title: 'Capital Requirements Updated', date: 'Jan 10, 2024', type: 'regulation' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.date}</p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-600 uppercase">
                        {item.type}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Licenses Tab */}
          <TabsContent value="licenses">
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">License Applications</CardTitle>
                <CardDescription>Track your license applications and status</CardDescription>
              </CardHeader>
              <CardContent>
                {licenses.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No license applications</p>
                    <p className="text-sm mt-1">Apply for your first banking license</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {licenses.map((license, idx) => (
                      <div key={idx} className="p-4 rounded-lg border border-border" data-testid={`license-item-${idx}`}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <h3 className="font-semibold">{license.institution_name}</h3>
                            <p className="text-sm text-muted-foreground capitalize">
                              {license.institution_type.replace('_', ' ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-mono text-sm">
                                ${(license.capital_amount / 1000000).toFixed(1)}M
                              </p>
                              <p className="text-xs text-muted-foreground">Capital</p>
                            </div>
                            <span className={`px-3 py-1 rounded text-xs font-medium uppercase tracking-wider ${
                              license.status === 'approved' ? 'status-active' :
                              license.status === 'pending_review' ? 'status-pending' :
                              'status-inactive'
                            }`}>
                              {license.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Regulatory Compliance</CardTitle>
                <CardDescription>Basel III and local regulatory requirements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {complianceMetrics.map((metric, idx) => (
                    <div key={idx} className="space-y-2" data-testid={`compliance-metric-${idx}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{metric.name}</span>
                        <span className={`text-sm font-mono ${
                          metric.status === 'compliant' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {metric.value}% {metric.status === 'compliant' && '✓'}
                        </span>
                      </div>
                      <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`absolute left-0 top-0 h-full rounded-full ${
                            metric.status === 'compliant' ? 'bg-green-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((metric.value / metric.requirement) * 50, 100)}%` }}
                        />
                        <div 
                          className="absolute top-0 h-full w-0.5 bg-primary"
                          style={{ left: '50%' }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0%</span>
                        <span>Requirement: {metric.requirement}%</span>
                        <span>200%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Liquidity Tab */}
          <TabsContent value="liquidity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Liquidity Position</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Total Liquidity', value: liquidityData.total_liquidity },
                      { label: 'Available Credit', value: liquidityData.available_credit },
                      { label: 'Reserve Balance', value: liquidityData.reserve_balance },
                      { label: 'Interbank Position', value: liquidityData.interbank_position }
                    ].map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-muted/30" data-testid={`liquidity-${idx}`}>
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-mono font-semibold">
                          ${(item.value / 1000000000).toFixed(2)}B
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Interbank Market</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                      <p className="text-sm text-muted-foreground mb-1">Overnight Rate</p>
                      <p className="font-mono text-3xl font-semibold">5.25%</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-1">1-Week Rate</p>
                        <p className="font-mono text-lg font-semibold">5.35%</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-1">1-Month Rate</p>
                        <p className="font-mono text-lg font-semibold">5.42%</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default InstitutionDashboard;
